<template>
    <Panel toggleable :collapsed="isAdminView" :pt="{
        header: { class: 'bg-primary card-header text-light' },
        toggler: { class: 'text-light hover:bg-primary-reverse' }
    }">
        <template #header>
            <div>
                <i class="mr-2 fas fa-shopping-cart" aria-hidden="true"></i>
                <span class="font-bold">Pending Cart</span>
            </div>
        </template>
        <ReturnCart :mtdId="mtdId" :isAdminView="isAdminView" />
    </Panel>
    <div class="vl-parent">
        <LoadingOverlay v-model:active="loading" :is-full-page="false" />
        <Panel :toggleable="openShipments.length > 0" :collapsed="openShipments.length == 0" class="mt-3" :pt="{
            header: { class: 'bg-primary card-header text-light' },
            toggler: { class: 'text-light hover:bg-primary-reverse' }
        }">
            <template #header>
                <div>
                    <i class="mr-2 fa-solid fa-box-open"></i>
                    <span class="font-bold">Open RMAs</span>
                    <span class="font-weight-bold ml-2">{{ openShipments?.length ?? 0 }}</span>
                </div>
            </template>
            <DataTable :value="openShipments" class="border" v-model:expandedRows="expandedShipmentRows" dataKey="id"
                stripedRows>
                <Column class="expander" :expander="true" headerStyle="width: 3em" />
                <Column field="id" header="RMA #"></Column>
                <Column field="requested_at" header="Date Submitted">
                    <template #body="slotProps">
                        <span>{{ moment(slotProps.data.requested_at).format('M/D/YY LT') }}</span>
                    </template>
                </Column>
                <Column field="delivery_date" header="Date Delivered">
                    <template #body="slotProps">
                        <span v-if="!slotProps.data.address_id">N/A</span>
                        <span v-else-if="slotProps.data.delivery_date">
                            {{ moment(slotProps.data.delivery_date).format('M/D/YY LT') }}</span>
                        <JetButton v-else-if="isAdminView" @click="receiveReturnShipment(slotProps.data.id)"><i
                                class="fa-solid fa-dolly mr-1"></i>Receive</JetButton>
                        <span v-else>Pending Delivery</span>
                    </template>
                </Column>
                <Column field="return_count" header="Return Items" headerClass="text-center" :pt="{
                    bodyCell: { class: 'text-center' },
                }"></Column>
                <Column field="warranty_count" header="Warranty Items" headerClass="text-center" :pt="{
                    bodyCell: { class: 'text-center' },
                }"></Column>
                <Column field="total_credit" header="Total" headerClass="text-right"
                    :pt="{ bodyCell: { class: 'text-right' }, }">
                    <template #body="slotProps">
                        <span>{{ formatDollars(slotProps.data.total_credit) }}</span>
                    </template>
                </Column>
                <template #expansion="slotProps">
                    <table class="border w-100 bg-white mt-2 mb-2">
                        <ReturnShipment :returnShipment="slotProps.data" submitted="true" :isAdminView="isAdminView"
                            @printPackingSlip="openPrintPackingSlip" @returnItemChanged="refresh" />
                    </table>
                </template>
            </DataTable>
        </Panel>
        <Panel :toggleable="completeShipments.length > 0" collapsed class="mt-3" :pt="{
            header: { class: 'bg-success card-header text-light' },
            toggler: { class: 'text-light hover:bg-primary-reverse' }
        }">
            <template #header>
                <div>
                    <i class="mr-2 fas fa-circle-check" aria-hidden="true"></i>
                    <span class="font-bold">Complete</span>
                    <span class="font-weight-bold ml-2">{{ completeShipments?.length ?? 0 }}</span>
                </div>
            </template>
            <DataTable :value="completeShipments" class="border" v-model:expandedRows="expandedShipmentRows" dataKey="id"
                stripedRows>
                <Column class="expander" :expander="true" headerStyle="width: 3em" />
                <Column field="id" header="RMA #"></Column>
                <Column field="requested_at" header="Date Submitted">
                    <template #body="slotProps">
                        <span>{{ moment(slotProps.data.requested_at).format('M/D/YYYY LT') }}</span>
                    </template>
                </Column>
                <Column field="delivery_date" header="Date Delivered">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.delivery_date">
                            {{ moment(slotProps.data.delivery_date).format('M/D/YY LT') }}</span>
                    </template>
                </Column>
                <Column field="return_count" header="Return Items" headerClass="text-center" :pt="{
                    bodyCell: { class: 'text-center' },
                }"></Column>
                <Column field="warranty_count" header="Warranty Items" headerClass="text-center" :pt="{
                    bodyCell: { class: 'text-center' },
                }"></Column>
                <Column field="total_credit" header="Total" headerClass="text-right"
                    :pt="{ bodyCell: { class: 'text-right' }, }">
                    <template #body="slotProps">
                        <span>{{ formatDollars(slotProps.data.total_credit) }}</span>
                    </template>
                </Column>
                <template #expansion="slotProps">
                    <table class="border w-100 bg-white mt-2 mb-2">
                        <template v-for="returnItem in slotProps.data.return_items">
                            <ReturnItem :returnItem="returnItem" :isAdminView="isAdminView" showOutcome="true" />
                        </template>
                    </table>
                </template>
            </DataTable>
        </Panel>
    </div>
    <teleport to="body">
        <PrintModal :printUrl="printUrl" @print="print" />
    </teleport>
</template>

<script>
import ReturnCart from '@/Components/Returns/ReturnCart.vue'
import ReturnItem from '@/Components/Returns/ReturnItem'
import ReturnShipment from '@/Components/Returns/ReturnShipment.vue';
import Panel from 'primevue/panel';
import moment from 'moment'
import JetButton from '@/Jetstream/Button'
import LoadingOverlay from '@/Components/General/LoadingOverlay.vue';
import PrintModal from '@/Components/Modals/PrintModal'

export default {
    components: {
        ReturnCart,
        Panel,
        JetButton,
        ReturnItem,
        ReturnShipment,
        LoadingOverlay,
        PrintModal,
    },
    props: {
        mtdId: Number,
        isAdminView: {
            type: Boolean,
            default: false,
        }
    },
    created() {
        this.moment = moment;
    },
    data() {
        return {
            loading: false,
            mtd: [],
            expandedShipmentRows: [],
            printingReturnShipmentId: null,
            printReport: '',
            printUrl: '',
        }
    },
    mounted() {
        this.refresh();
    },
    computed: {
        mtdDataRoute() {
            return this.isAdminView ? 'admin.returns.mtddata' : 'mtd.return.mtdData'
        },
        openShipments() {
            return this.mtd?.return_shipments?.filter((returnShipment) => returnShipment.status != 'Complete').sort((b, a) => a.id - b.id) ?? [];
        },
        completeShipments() {
            return this.mtd?.return_shipments?.filter((returnShipment) => returnShipment.status == 'Complete').sort((b, a) => a.id - b.id) ?? [];
        },
    },
    methods: {
        refresh() {
            this.loading = true
            axios.get(this.route(this.mtdDataRoute, this.mtdId))
                .then((response) => {
                    if (response.data.success) {
                        this.mtd = response.data.mtd;
                    }
                    this.loading = false
                });
        },
        receiveReturnShipment(returnShipmentId) {
            this.loading = true
            axios.get(this.route('admin.returns.receiveShipment', returnShipmentId))
                .then((response) => {
                    if (response.data.success) {
                        this.mtd = response.data.mtd;
                    }
                    this.loading = false
                });
        },
        openPrintPackingSlip(returnShipmentId) {
            this.printReport = 'mtd.return.packingslip'
            this.printingReturnShipmentId = returnShipmentId
            this.openPrintDialog();
        },
        openPrintDialog() {
            this.setPrintUrl(0);
            $('#PrintModal').modal('show');
        },
        print() {
            this.setPrintUrl(1);
        },
        setPrintUrl(print) {
            this.printUrl = this.route(this.printReport, { returnShipment: this.printingReturnShipmentId, print: print });
        },
    },
}

</script>

<style>
.p-column-header-content {
    display: block !important;
}

.p-datatable-row-expansion {
    background-color: lightgray !important;
}
</style>